import { calculateTravelTotal } from "./flightPricing";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

export const cx = (...classes: string[]) => {
  return classes.filter(Boolean).join(" ");
};

export const formatDate = (date: string, format: "summary" | "short" | "long" = "short") => {
  const rawDate = dayjs(date);
  if (!rawDate || !rawDate.isValid()) {
    return "";
  }
  if (format === "summary") {
    return rawDate.fromNow();
  } else if (format === "short") {
    return rawDate.format("DD MMM YYYY");
  } else {
    return rawDate.format("DD MMM YYYY, hh:mm A");
  }
};

export const formatCurrency = (num: number, currency: string = "INR") => {
  const usdCurrencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  });
  return usdCurrencyFormatter.format(num || 0);
};

export const summarizeCurrency = (num: number) => {
  const usdCurrencyFormatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    notation: "compact",
  });
  return usdCurrencyFormatter.format(num);
};

export const capitalize = (s: string) => {
  return s ? s.charAt(0).toUpperCase() + s.substring(1).toLowerCase() : "";
};
export const titleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ");
};
export const splitCamelCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace("_", " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const mergeObjects = (obj1: Record<string, unknown>, obj2: Record<string, unknown>): Record<string, unknown> => {
  const merged = { ...obj1 };
  for (const key in obj2) {
    if (obj2[key] !== undefined) {
      merged[key] = obj2[key];
    }
  }
  return merged;
};

// Status utility functions
export const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "new":
      return "blue";
    case "pending":
      return "blue";
    case "in-progress":
      return "orange";
    case "on-hold":
      return "orange";
    case "confirmed":
      return "green";
    case "booked":
      return "green";
    case "ongoing":
      return "purple";
    case "changed":
      return "purple";
    case "completed":
      return "green";
    case "cancelled":
      return "red";
    default:
      return "default";
  }
};

// Booking status utility functions
export type BookingStatus = "pending" | "in-progress" | "booked" | "changed" | "cancelled";

export const getBookingStatusStyles = (status?: BookingStatus) => {
  switch (status) {
    case "pending":
      return "bg-orange-100 border border-orange-200 text-orange-800";
    case "in-progress":
      return "bg-blue-100 border border-blue-200 text-blue-800";
    case "booked":
      return "bg-green-100 border border-green-200 text-green-800";
    case "changed":
      return "bg-purple-100 border border-purple-200 text-purple-800";
    case "cancelled":
      return "bg-red-100 border border-red-200 text-red-800";
    default:
      return "bg-gray-100 border border-gray-200 text-gray-800";
  }
};

export const getBookingStatusColor = (status?: BookingStatus) => {
  switch (status) {
    case "pending":
      return "orange";
    case "in-progress":
      return "blue";
    case "booked":
      return "green";
    case "changed":
      return "purple";
    case "cancelled":
      return "red";
    default:
      return "default";
  }
};

// Payment status utility functions
export type PaymentStatus = "paid" | "partial" | "pending" | "overdue" | "verified" | "junk" | "cancelled";

export const getPaymentStatusDisplay = (status: PaymentStatus) => {
  switch (status) {
    case "paid":
      return {
        color: "green",
        text: status,
        className: "bg-green-100 border border-green-200 text-green-800",
      };
    case "partial":
      return {
        color: "blue",
        text: status,
        className: "bg-blue-100 border border-blue-200 text-blue-800",
      };
    case "pending":
      return {
        color: "orange",
        text: status,
        className: "bg-orange-100 border border-orange-200 text-orange-800",
      };
    case "overdue":
      return {
        color: "red",
        text: status,
        className: "bg-red-100 border border-red-200 text-red-800",
      };
    case "verified":
      return {
        color: "purple",
        text: status,
        className: "bg-purple-100 border border-purple-200 text-purple-800",
      };
    case "junk":
      return {
        color: "default",
        text: status,
        className: "bg-gray-100 border border-gray-200 text-gray-500",
      };
    case "cancelled":
      return {
        color: "default",
        text: "Cancelled",
        className: "bg-gray-50 border border-gray-200 text-gray-500",
      };
    default:
      return {
        color: "default",
        text: "Unknown",
        className: "bg-gray-100 border border-gray-200 text-gray-800",
      };
  }
};

/**
 * Calculate total tax amount based on itinerary data and tax configuration
 * @param itinerary Itinerary data containing costs, markup, taxes, and travel details
 * @returns Total tax amount
 */
export const calculateTotalTaxAmount = (itinerary: {
  taxes?: Array<{ name?: string; appliedOn: string; percentage: number }>;
  overrideTotalCostEnabled?: boolean;
  overrideTotalCost?: number;
  totalCost?: number;
  markupAmount?: number;
  travelMarkupAmount?: number;
  travel?: Array<Record<string, unknown>>;
}): number => {
  if (!itinerary) return 0;

  return calculateTaxAmounts(itinerary).reduce((total, taxAmount) => total + taxAmount, 0);
};

/**
 * Calculate individual tax amounts based on itinerary data and tax configuration.
 * Special rule: TCS is always calculated on (selected base + GST amount).
 * @param itinerary Itinerary data containing costs, markup, taxes, and travel details
 * @returns Tax amounts in the same order as provided taxes
 */
export const calculateTaxAmounts = (itinerary: {
  taxes?: Array<{ name?: string; appliedOn: string; percentage: number }>;
  overrideTotalCostEnabled?: boolean;
  overrideTotalCost?: number;
  totalCost?: number;
  markupAmount?: number;
  travelMarkupAmount?: number;
  travel?: Array<Record<string, unknown>>;
}): number[] => {
  if (!itinerary) return [];

  const selectedTaxes = itinerary.taxes || [];
  const baseCost = itinerary.overrideTotalCostEnabled ? itinerary.overrideTotalCost : itinerary.totalCost;
  const markupAmount = itinerary.markupAmount || 0;
  const travelMarkupAmount = itinerary.travelMarkupAmount || 0;
  const travelTotal = calculateTravelTotal(itinerary.travel);
  const baseCostWithoutTravel = Math.max(0, (baseCost || 0) - travelTotal);

  const getTaxBaseAmount = (tax: { appliedOn: string; percentage: number }) => {
    switch (tax.appliedOn) {
      case "cost":
        return baseCostWithoutTravel;
      case "markup":
        return markupAmount + travelMarkupAmount;
      case "cost+markup":
        return baseCostWithoutTravel + markupAmount + travelMarkupAmount;
      default:
        return baseCostWithoutTravel;
    }
  };

  const normalizeTaxName = (name?: string) => (name || "").trim().toLowerCase();
  const gstAmount = selectedTaxes.reduce((total, tax) => {
    if (normalizeTaxName(tax.name) !== "gst") return total;
    const baseAmount = getTaxBaseAmount(tax);
    return total + (baseAmount * Number(tax.percentage || 0)) / 100;
  }, 0);

  return selectedTaxes.map((tax) => {
    const baseAmount = getTaxBaseAmount(tax);
    const isTcs = normalizeTaxName(tax.name) === "tcs";
    const effectiveBaseAmount = isTcs ? baseAmount + gstAmount : baseAmount;
    return (effectiveBaseAmount * Number(tax.percentage || 0)) / 100;
  });
};
